import type { TimelineItem } from '~/transformers/Dato/TimelineTransformer';

export const isValidModeForDirections = (modeOfTransport: string): boolean => {
  return [
    'RAIL',
    'COACH',
    'SLEEPER',
    'BUS',
    'FERRY',
    'TAXI',
    'TRAIN',
    'TRAM',
    'WALK',
    'OVERNIGHT_BUS',
    'OVERNIGHT_FERRY'
  ].includes(modeOfTransport);
};

export const makeGoogleDirectionsURL = (start: string | undefined, end: string | undefined): string => {
  if (!start || !end) return '';
  return `https://www.google.com/maps/dir/?api=1&origin=${start}&destination=${end}&travelmode=transit`;
};

// If we don't do this, typos in the CMS will result in silly directions being shown
export const fuzzyPlaceMatcher = (string: string): string => {
  return string
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase();
};

type DirectionsURLs = {
  [key: number]: {
    arrive?: string | null;
    depart?: string | null;
    transfer?: string | null;
    from?: string;
    to?: string;
  };
};

export const makeDirectionsURLs = (items: TimelineItem[]): DirectionsURLs => {
  const directionsURLs: DirectionsURLs = {};

  items.forEach((currentLeg, index) => {
    if (currentLeg.type === 'Stay') {
      // initialize object in directions obj at that index
      directionsURLs[index] = {
        arrive: null,
        depart: null
      };
      // STAY
      const firstBooking = currentLeg.accommodationBookings?.[0]?.accommodation;

      const lastBooking =
        currentLeg.accommodationBookings?.[currentLeg.accommodationBookings.length - 1]?.accommodation;

      // PREVIOUS LEG
      const prevLeg = items[index - 1];
      if (
        !!prevLeg &&
        prevLeg.type === 'Journey' &&
        firstBooking?.address &&
        firstBooking?.title !== 'Your own accommodation'
      ) {
        const prevLegMode = prevLeg.modeOfTransports?.[prevLeg.modeOfTransports.length - 1];
        const prevLegDestination =
          prevLeg.destinationTerminalName && prevLeg.destinationTerminalName !== 'TBC'
            ? prevLeg.destinationTerminalName
            : prevLeg.destination;

        const arriveURL =
          prevLegMode && isValidModeForDirections(prevLegMode)
            ? makeGoogleDirectionsURL(prevLegDestination, firstBooking?.address)
            : null;

        directionsURLs[index].arrive = arriveURL;
      }

      // NEXT LEG
      const nextLeg = items[index + 1];
      if (
        !!nextLeg &&
        nextLeg.type === 'Journey' &&
        lastBooking?.address &&
        lastBooking.title !== 'Your own accommodation'
      ) {
        const nextLegMode = nextLeg.modeOfTransports?.[0];
        const nextLegOrigin =
          nextLeg.originTerminalName && nextLeg.originTerminalName !== 'TBC'
            ? nextLeg.originTerminalName
            : nextLeg.origin;

        const departURL =
          nextLegMode && isValidModeForDirections(nextLegMode)
            ? makeGoogleDirectionsURL(lastBooking?.address, nextLegOrigin)
            : null;

        directionsURLs[index].depart = departURL;
      }
    }

    if (currentLeg.type === 'Journey') {
      if (index === items.length - 1) return; // if there can't be a next leg because this is the last leg in the array, do nothing
      const nextLeg = items[index + 1];
      if (nextLeg.type !== 'Journey') return;
      if (!currentLeg.destinationTerminalName || !nextLeg.originTerminalName) return;
      if (fuzzyPlaceMatcher(currentLeg.destinationTerminalName) === fuzzyPlaceMatcher(nextLeg.originTerminalName))
        return;

      const currentLegMode = currentLeg.modeOfTransports?.[0];
      const nextLegMode = nextLeg.modeOfTransports?.[0];

      const transferURL =
        currentLegMode &&
        nextLegMode &&
        isValidModeForDirections(currentLegMode) &&
        isValidModeForDirections(nextLegMode)
          ? makeGoogleDirectionsURL(currentLeg.destinationTerminalName, nextLeg.originTerminalName)
          : null;

      directionsURLs[index] = {
        transfer: transferURL,
        from: currentLeg.destinationTerminalName,
        to: nextLeg.originTerminalName
      };
    }
  });

  return directionsURLs;
};
